// ---------------------------------------------------------------------------
// The banners of a page. An admin removes the banners a period does not need
// (`hiddenTemplates`) and, on releases, adds banners of other sets
// (`extraTemplates`, utils/releases.js). Both are scene fields: a default
// scene carries them per stage, product, set and layout, and a saved scene
// keeps its own, so a later change of the defaults does not change it.
//
// A removed banner is not drawn at all, not even as the "Hidden for export"
// placeholder of a banner that the eye hides. So a user cannot turn it back
// on, and an export does not include it: the renderer captures the banners
// that a page draws.
//
// An added banner is on when a default loads. The eyes of a default can be
// older than the banners it adds, and such eyes leave a banner of another set
// off (data/defaultSceneVisibility.ts).
// ---------------------------------------------------------------------------

/** A banner of a page list. ALL-products instances name their template. */
export interface BannerLike {
  id: string;
  templateId?: string;
}

/** `list` without the removed banners, in its order. */
export function withoutHiddenTemplates<Banner extends BannerLike>(
  list: Banner[],
  hiddenTemplateIds: readonly string[] | null | undefined
): Banner[] {
  if (!hiddenTemplateIds || hiddenTemplateIds.length === 0) return list;
  const hidden = new Set(hiddenTemplateIds);
  return list.filter((banner) => !hidden.has(banner.templateId ?? banner.id));
}

/** `ids` with `id` in it or out of it, in its order. */
export function withTemplateId(ids: readonly string[], id: string, included: boolean): string[] {
  const rest = ids.filter((item) => item !== id);
  return included ? [...rest, id] : rest;
}

/** The ids of `next` that `previous` does not have, in their order. */
export function addedTemplateIds(previous: readonly string[], next: readonly string[]): string[] {
  const known = new Set(previous);
  return next.filter((id) => !known.has(id));
}

/** Where a page draws its banners: once each, or once per product (ALL). */
export interface BannerListScope {
  isAllProducts: boolean;
  /** The products of the ALL-products view. */
  productCodes: readonly string[];
}

/**
 * The ids that banners added from another set take in the `visible` and
 * `order` lists: the template id, or on the ALL-products view one instance
 * per product, `<templateId>-<code>` (utils/releases.js).
 */
export function extraTemplateListIds(
  templateIds: readonly string[],
  {isAllProducts, productCodes}: BannerListScope
): string[] {
  if (!isAllProducts) return [...templateIds];
  return productCodes.flatMap((code) => templateIds.map((templateId) => `${templateId}-${code}`));
}

/**
 * `visible` with the banners added from other sets on, after the ids it has.
 * The same list when each added banner is on already.
 */
export function withExtraTemplatesVisible(
  visible: string[],
  extraTemplateIds: readonly string[] | null | undefined,
  scope: BannerListScope
): string[] {
  if (!extraTemplateIds || extraTemplateIds.length === 0) return visible;
  const on = new Set(visible);
  const added = extraTemplateListIds(extraTemplateIds, scope).filter((id) => !on.has(id));
  return added.length > 0 ? [...visible, ...added] : visible;
}
